import { useLocation } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';
import { parse } from 'query-string';
import React, { FC, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import Button, { IButtonProps } from '@ui/atoms/Button';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import TextButton from '@ui/atoms/TextButton';

import ExternalLink from '@ui/molecules/ExternalLink';

import fullWidth from '@ui/styled/FullWidth';

import { interpolate, keysToCamel } from '@utils/butter';
import { resendRegistrationCode } from '@utils/flowHelpers';
import { get } from '@utils/indexedDb';
import { mapKeys } from '@utils/mapKeys';

import FlowContext from '@providers/FlowContextProvider';

import { IFlowStepProps } from '@folx-types/flow-steps';

import theme from '@theme/FolxTheme';

interface IMembershipCheckEmailStepProps extends IFlowStepProps {}

interface IMembershipCheckEmailStepContent {
	title: string;
	subtitle: string;
	cta: string;
	secondaryCta: string;
	secondaryCtaLabel: string;
	reSentMessage: string;
	help: string;
	helpCta: string;
}

const ExtLink = styled(ExternalLink)`
	color: ${theme.tints.darken40(theme.colors.cyan)};
`;

const FullWidthButton = fullWidth<IButtonProps>(Button);
const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);
const AlignedText = styled(BasicText)`
	display: flex;
	justify-content: center;
	gap: 1ch;
`;

const MembershipCheckEmailStep: FC<IMembershipCheckEmailStepProps> = ({ stepKey }) => {
	const location = useLocation();
	const [codeResent, setCodeResent] = useState(false);
	const [sendingCode, setSendingCode] = useState(false);
	const [email, setEmail] = useState('');

	const { pageContent } = useStaticQuery<Record<string, string>>(
		graphql`
			{
				pageContent: butterPage(slug: { eq: "mssp-membership-check-email" }) {
					title
					subtitle
					cta
					secondary_cta
					secondary_cta_label
					re_sent_message
					help
					help_cta
				}
			}
		`
	);

	const { title, subtitle, cta, secondaryCta, secondaryCtaLabel, reSentMessage, help, helpCta } =
		keysToCamel(pageContent) as IMembershipCheckEmailStepContent;

	const { handleNavBack } = useContext(FlowContext);

	useEffect(() => {
		(async () => {
			const user = await get('user');
			setEmail(user.email);
		})();
	}, []);

	const resendEmail = async () => {
		if (!codeResent && !sendingCode) {
			setSendingCode(true);
			const { email } = await get('user');
			const { source } = mapKeys((s) => s, parse(location.search, { parseNumbers: true }));
			await resendRegistrationCode(email, typeof source === 'string' ? source : undefined);
			setCodeResent(true);
			setSendingCode(false);
		}
	};

	return (
		<FullWidthColumn gap="lg">
			<HeadingText>{title}</HeadingText>
			{!codeResent ? (
				<>
					<FullWidthColumn gap="sm">
						<BasicText>{interpolate(subtitle, { email })}</BasicText>
					</FullWidthColumn>
					<FullWidthColumn gap="sm">
						<FullWidthButton label={cta} onClick={() => handleNavBack(stepKey)} />

						<AlignedText>
							{secondaryCtaLabel}
							<TextButton
								variant="underline"
								size="sm"
								label={secondaryCta}
								onClick={resendEmail}
								disabled={sendingCode}
							/>
						</AlignedText>
					</FullWidthColumn>
				</>
			) : (
				<>
					<BasicText>{reSentMessage}</BasicText>
					<BasicText variant="fineprint">
						{help} <ExtLink href="https://support.folxhealth.com/hc/en-us" label={helpCta} />.
					</BasicText>
				</>
			)}
		</FullWidthColumn>
	);
};

export default MembershipCheckEmailStep;
